import { Alert, Snackbar } from "@mui/material";
import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";

import {
  WebSocketContext,
  type WebSocketContextType,
} from "../../contexts/web-socket-context";
import { useWebSocket } from "../../hooks/useWebSocket";
import { resetAisDataFetched } from "../../store/slices/aisVessels";
import { setCrewProgress, setCrewComplete, setCrewFailed } from "../../store/slices/crew";
import { addNewIncomingCallData, updateIncomingCallData } from "../../store/slices/incomingCalls";
import {
  setCsofProgress,
  setCsofComplete,
  setCsofFailed,
  setCsofFormatUnknown,
} from "../../store/slices/sofDocuments";
import { resetDataFetched as resetVesselDataFetched } from "../../store/slices/vesselList";
import { toogleNewJobsAvailable } from "../../store/slices/voyages";
import { addWarning, incrementUnreadCount } from "../../store/slices/warnings";
import { fetchAisVessels } from "../../store/thunks/aisVesselsThunks";
import { fetchVesselListData } from "../../store/thunks/vesselThunk";
import {
  fetchJobsListWithBerthData,
  fetchJobsListWithBerthDataStatic,
} from "../../store/thunks/voyagesThunks";

import type { AppDispatch } from "../../store/store";
import type { WebSocketMessage } from "../../utils/types/socket";

interface CallRecordUpdateData {
  callId: string;
  transcriptionId: string;
  audioUrl?: string;
  userId?: string;
  tags?: string[];
}

interface ToastState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info";
}

interface WebSocketProviderProps {
  children: React.ReactNode;
}

const socketUrl = import.meta.env.VITE_SOCKET_URL;
const socketDelayDuration = import.meta.env.VITE_SOCKET_DELAY_DURATION || 100; // milliseconds

const storeMap = {
  phonesStore: "phoneList",
  vesselsStore: "vesselList",
  peopleStore: "peopleList",
  companiesStore: "companyList",
  locationsStore: "locationList",
  emailStore: "emailHistoryList",
  shiftingmatrixStore: "shifting",
  portCallingsStore: "portCalls",
  channelclosureStore: "closureList",
  accountingStore: "billingCodeList",
  bunkersFreshWaterStore: "bunker",
  cargoStore: "cargo",
  berthsStore: "voyageSchedule",
  deliveryvendorStore: "delivery",
  onboardservicesStore: "onBoard",
  jobsActiveStore: ["voyages", "clientDetails"],
  jobnotificationlist: ["voyages"],
  directbillingassignment: "directBilling",
  gates: "gates",
  pilotcharge: "pilotCharge",
  agencyfee: "agencyFee",
  dockagefee: "dockageFee",
  callsblacklist: "callsBlacklistList",
};

const WebSocket: React.FC<WebSocketProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const lastToastRef = useRef<number>(0);
  const TOAST_DEBOUNCE = 3000;

  // Store callbacks in refs to avoid dependency changes
  const callbacksRef = useRef({
    showToast: (_message: string, _severity: ToastState["severity"]) => {},
    handleMessage: (_data: WebSocketMessage) => {},
  });

  // Changed: Use array to preserve ALL events, not just latest per store
  const storeChangeQueueRef = useRef<WebSocketMessage[]>([]);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aisUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRefreshReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    severity: "info",
  });

  const sendRef = useRef<((message: any) => boolean) | null>(null);

  const showToast = useCallback(
    (message: string, severity: ToastState["severity"]) => {
      const now = Date.now();
      if (now - lastToastRef.current > TOAST_DEBOUNCE) {
        setToast({ open: true, message, severity });
        lastToastRef.current = now;
      }
    },
    []
  );

  const handleCloseToast = useCallback(() => {
    setToast((prev: any) => ({ ...prev, open: false }));
  }, []);

  const reloadStore = useCallback(
    (storeId: string, event: WebSocketMessage) => {
      const sliceName = storeMap[storeId as keyof typeof storeMap] || storeId;

      // Create a thunk action creator
      if (Array.isArray(sliceName)) {
        sliceName.forEach((name) => {
          const thunkAction =
            () => (thunkDispatch: AppDispatch, getState: any) => {
              const currentRootState = getState();
              return thunkDispatch({
                type: `${name}/${event.op}`,
                payload: {
                  ...event,
                  rootState: currentRootState,
                },
              });
            };
          (dispatch as AppDispatch)(thunkAction());
        });
        return;
      }

      const thunkAction = () => (thunkDispatch: AppDispatch, getState: any) => {
        const currentRootState = getState();
        console.log(
          "Dispatching action for slice:",
          sliceName,
          "with event:",
          event
        );
        return thunkDispatch({
          type: `${sliceName}/${event.op}`,
          payload: {
            ...event,
            rootState: currentRootState,
          },
        });
      };

      (dispatch as AppDispatch)(thunkAction());
    },
    [dispatch]
  );

  const processBatchedStoreChanges = useCallback(() => {
    const queuedChanges = [...storeChangeQueueRef.current];
    storeChangeQueueRef.current = []; // Clear array

    console.log(`Processing ${queuedChanges.length} store changes`);

    // Process ALL events in chronological order to preserve data integrity
    queuedChanges.forEach((data, index) => {
      const { storeId } = data;
      if (!storeId) return;

      console.log(`Processing event ${index + 1}/${queuedChanges.length}:`, {
        storeId,
        op: data.op,
      });

      if (Array.isArray(storeId)) {
        storeId.forEach((id) => reloadStore(id, data));
      } else {
        reloadStore(storeId, data);
      }
    });
  }, [reloadStore]);

  const storeChangeHandler = useCallback(
    (data: WebSocketMessage) => {
      const { storeId, tabId } = data;
      if (!storeId) return;

      const sessionTabId = window.sessionStorage.getItem("tabId");
      if (!sessionTabId || sessionTabId !== tabId || !tabId) {
        // Add timestamp to preserve chronological order
        const eventWithTimestamp = {
          ...data,
          timestamp: Date.now(),
        };

        // Push to array to preserve ALL events
        storeChangeQueueRef.current.push(eventWithTimestamp);

        // Clear existing timeout and set a new one
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
          processBatchedStoreChanges();
          debounceTimeoutRef.current = null;
        }, socketDelayDuration as number);
      }
    },
    [processBatchedStoreChanges]
  );

  const handleCallRecordUpdate = useCallback((data: WebSocketMessage) => {
    console.log("Call record updated:", data);
    if (data.userId) {
      const event = new CustomEvent("callRecordUpdated", {
        detail: {
          callId: data.callId,
          transcriptionId: data.transcriptionId,
          userId: data.userId,
        },
      });
      window.dispatchEvent(event);
    }
  }, []);

  const handleCallIssue = useCallback(
    (data: WebSocketMessage) => {
      console.log("Call issue received:", data);
      const warningMessage =
        typeof data.message === "string"
          ? data.message
          : data.message?.message || "A call issue was detected";

      // Create a warning from the call issue
      const warning = {
        id: data.id || `warning-${Date.now()}`,
        type: data.type || "Call",
        identified: data.identified || new Date().toISOString(),
        message: warningMessage,
        seen: false,
      };
      dispatch(addWarning(warning));
      dispatch(incrementUnreadCount());
    },
    [dispatch]
  );

  const handleIncomingCall = useCallback(
    (data: WebSocketMessage) => {
      console.log("Incoming call received:", data);

      // Check if this is a call event with the expected structure
      if (data.storeId === "call" && data.record) {
        const { record } = data;

        // Only process inbound calls
        if (record.direction === "inbound" && record.from) {
          if (data.callEventType === "CALL_RINGING")
            dispatch(
              addNewIncomingCallData({
                id: record.call_id || `${record.from}-${Date.now()}`,
                from: record.from,
                to: record.to || "",
                time: record.created_dt || new Date().toISOString(),
                status: record.status || "ringing",
              })
            );
          else if (data.callEventType === "CALL_IN_PROGRESS")
            dispatch(
              updateIncomingCallData({
                id: record.call_id,
                status: record.status || "in-progress",
              })
            );
          else if (data.callEventType === "CALL_COMPLETED")
            dispatch(
              updateIncomingCallData({
                id: record.call_id,
                status: record.status || "completed",
              })
            );
        }
      }
    },
    [dispatch]
  );

  const handleAisUpdate = useCallback(
    (data: WebSocketMessage) => {
      console.log("[AIS WS] Refresh requested:", data);

      if (aisUpdateTimeoutRef.current) {
        clearTimeout(aisUpdateTimeoutRef.current);
      }

      aisUpdateTimeoutRef.current = setTimeout(() => {
        dispatch(resetAisDataFetched());
        dispatch(resetVesselDataFetched());
        (dispatch as AppDispatch)(fetchAisVessels());
        (dispatch as AppDispatch)(fetchVesselListData());
        (dispatch as AppDispatch)(
          fetchJobsListWithBerthDataStatic({
            page: 0,
            pageSize: 500,
          })
        );
        dispatch(toogleNewJobsAvailable(true));
        aisUpdateTimeoutRef.current = null;
      }, Number(socketDelayDuration) || 100);
    },
    [dispatch]
  );

  const handleMessage = useCallback(
    (data: WebSocketMessage) => {
      if (data.type === "ais_update" || data.event === "ais_update") {
        handleAisUpdate(data);
        return;
      }

      if (
        (data.type === "data" || data.type === "stream_complete") &&
        data.sessionId
      ) {
        // This is a chat message. Broadcast it as a custom event.
        // The event name is specific to the session, so only the correct chat window will listen.
        console.log(`Broadcasting chat event for session: ${data.sessionId}`);
        window.dispatchEvent(
          new CustomEvent(`chat-stream-${data.sessionId}`, { detail: data })
        );
        return;
      }

      // Handle direct call events (type: CALL_RINGING, CALL_IN_PROGRESS, CALL_COMPLETED)
      if (data.type === "CALL_RINGING" && data.payload) {
        console.log("Incoming call event (CALL_RINGING):", data);
        const { payload } = data;
        if (payload.direction === "inbound" && payload.from) {
          dispatch(
            addNewIncomingCallData({
              id: payload.sid || `${payload.from}-${Date.now()}`,
              from: payload.from,
              to: payload.to || "",
              time: payload.timestamp ? new Date(payload.timestamp).toISOString() : new Date().toISOString(),
              status: payload.status || "ringing",
            })
          );
        }
        return;
      }

      if (data.type === "CALL_IN_PROGRESS" && data.payload) {
        console.log("Call in progress event:", data);
        const { payload } = data;
        dispatch(
          updateIncomingCallData({
            id: payload.sid,
            status: payload.status || "in-progress",
          })
        );
        return;
      }

      if (data.type === "CALL_COMPLETED" && data.payload) {
        console.log("Call completed event:", data);
        const { payload } = data;
        dispatch(
          updateIncomingCallData({
            id: payload.sid,
            status: payload.status || "completed",
          })
        );
        return;
      }

      // Detect CSOF pipeline events from any envelope format:
      // 1) { event: "sofEvent", message: { event: "csof_progress", ... } }
      // 2) { message: "{ \"event\": \"csof_progress\", ... }" }  (JSON string)
      // 3) { event: "csof_progress", csof_id: "..." }  (flat)
      let msgPayload = (data as any).message;
      // Backend may send message as a JSON string — parse it
      if (typeof msgPayload === "string") {
        try {
          msgPayload = JSON.parse(msgPayload);
        } catch {
          // Not JSON — keep as-is
        }
      }
      const csofEventName =
        (typeof msgPayload === "object" && msgPayload?.event) ||
        (typeof data.event === "string" ? data.event : null);
      const isCsofEvent =
        csofEventName === "csof_progress" ||
        csofEventName === "csof_complete" ||
        csofEventName === "csof_failed" ||
        csofEventName === "csof_format_unknown";

      if (isCsofEvent) {
        const csofPayload =
          typeof msgPayload === "object" && msgPayload?.csof_id ? msgPayload : data;
        console.log("[CSOF WS]", csofEventName, csofPayload);
        if (csofEventName === "csof_progress") {
          dispatch(setCsofProgress(csofPayload));
        } else if (csofEventName === "csof_complete") {
          dispatch(setCsofComplete(csofPayload));
        } else if (csofEventName === "csof_failed") {
          dispatch(setCsofFailed(csofPayload));
        } else if (csofEventName === "csof_format_unknown") {
          dispatch(setCsofFormatUnknown(csofPayload));
        }
        return; // Handled — skip the switch
      }

      // Crew pipeline events: crew_progress, crew_complete, crew_failed
      const isCrewEvent =
        csofEventName === "crew_progress" ||
        csofEventName === "crew_complete" ||
        csofEventName === "crew_failed";

      if (isCrewEvent) {
        const crewPayload =
          typeof msgPayload === "object" && msgPayload?.job_id
            ? msgPayload
            : data;
        console.log("[CREW WS]", csofEventName, crewPayload);
        if (csofEventName === "crew_progress") {
          dispatch(setCrewProgress(crewPayload));
        } else if (csofEventName === "crew_complete") {
          dispatch(setCrewComplete(crewPayload));
        } else if (csofEventName === "crew_failed") {
          dispatch(setCrewFailed(crewPayload));
        }
        return;
      }

      // Gate Access pipeline events: gateaccess_progress, gateaccess_complete, gateaccess_failed
      const isGateAccessEvent =
        csofEventName === "gateaccess_progress" ||
        csofEventName === "gateaccess_complete" ||
        csofEventName === "gateaccess_failed";

      if (isGateAccessEvent) {
        const gaPayload = typeof msgPayload === "object" ? msgPayload : data;
        console.log("[GATEACCESS WS]", csofEventName, gaPayload);
        if (csofEventName === "gateaccess_complete") {
          window.dispatchEvent(
            new CustomEvent("gateAccessDocumentReady", {
              detail: {
                documentId: gaPayload?.data?.document_id,
                fileName: gaPayload?.data?.file_name,
                fileUrl: gaPayload?.data?.file_url,
                companyName: gaPayload?.company_name,
              },
            })
          );
          showToast(
            `Gate list for ${gaPayload?.company_name || "company"} is ready.`,
            "success"
          );
        } else if (csofEventName === "gateaccess_failed") {
          window.dispatchEvent(
            new CustomEvent("gateAccessGenerationFailed", {
              detail: { message: gaPayload?.message || "Gate list generation failed." },
            })
          );
          showToast(
            gaPayload?.message || "Gate list generation failed.",
            "error"
          );
        }
        return;
      }

      switch (data.event) {
        case "storeChange":
          // Detect completed gate-list documents from the document storeChange fallback
          if (
            data.storeId === "document" &&
            data.op === "POST" &&
            typeof data.record?.file_name === "string" &&
            data.record.file_name.startsWith("gate-list_")
          ) {
            window.dispatchEvent(
              new CustomEvent("gateAccessDocumentReady", {
                detail: {
                  documentId: data.record?.document_id,
                  fileName: data.record?.file_name,
                  fileUrl: undefined, // will be fetched by the component
                  companyName: undefined,
                },
              })
            );
          }
          // Handle incoming calls from storeChange events
          if (data.storeId === "call") {
            handleIncomingCall(data);
          }
          storeChangeHandler(data);
          break;
        case "sofEvent":
          // Fallback for sofEvent wrapper without recognized sub-event — log it
          console.log("SOF event (unrecognized sub-event):", data);
          break;
        case "callRecordUpdated":
          handleCallRecordUpdate(data);
          break;
        case "callIssue":
          handleCallIssue(data);
          break;
        case "loginSuccess":
          break;
        default:
          console.warn("Unhandled WebSocket event:", data.event, "Full data:", data);
      }
    },
    [
      dispatch,
      storeChangeHandler,
      handleCallRecordUpdate,
      handleCallIssue,
      handleIncomingCall,
      handleAisUpdate,
      showToast,
    ]
  );

  // Update callbacks ref whenever they change
  useEffect(() => {
    callbacksRef.current = {
      showToast,
      handleMessage,
    };
  }, [showToast, handleMessage]);

  // Static config that never changes - this prevents reconnections
  const wsConfig = useMemo(
    () => ({
      url: socketUrl || "",
      getToken: () => localStorage.getItem("tmaWebAppServiceToken") || "",
      onOpen: () => {
        callbacksRef.current.showToast("Live updates is online.", "success");
      },
      onClose: () => {
        callbacksRef.current.showToast("Live updates is offline.", "info");
      },
      onError: () => {
        callbacksRef.current.showToast(
          "Error with live updates connection.",
          "error"
        );
      },
      onDisable: () => {
        callbacksRef.current.showToast("Live updates is disabled.", "info");
      },
      onMessage: (data: WebSocketMessage) => {
        callbacksRef.current.handleMessage(data);
      },
    }),
    [] // Empty dependencies - config never changes
  );

  const { send, reconnect, isConnected, waitUntilConnected } = useWebSocket(wsConfig);

  useEffect(() => {
    if (send) {
      sendRef.current = send;
    }
  }, [send]);

  useEffect(() => {
    const handleTokenRefreshed = () => {
      if (tokenRefreshReconnectTimeoutRef.current) {
        return;
      }

      tokenRefreshReconnectTimeoutRef.current = setTimeout(() => {
        tokenRefreshReconnectTimeoutRef.current = null;
        reconnect();
      }, 250);
    };

    window.addEventListener("auth:tokenRefreshed", handleTokenRefreshed);
    return () => {
      window.removeEventListener("auth:tokenRefreshed", handleTokenRefreshed);
      if (tokenRefreshReconnectTimeoutRef.current) {
        clearTimeout(tokenRefreshReconnectTimeoutRef.current);
        tokenRefreshReconnectTimeoutRef.current = null;
      }
    };
  }, [reconnect]);

  useEffect(
    () => () => {
      // Cleanup on unmount
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (aisUpdateTimeoutRef.current) {
        clearTimeout(aisUpdateTimeoutRef.current);
      }
      if (tokenRefreshReconnectTimeoutRef.current) {
        clearTimeout(tokenRefreshReconnectTimeoutRef.current);
      }
      storeChangeQueueRef.current = []; // Clear array
      setToast({ open: false, message: "", severity: "info" });
    },
    []
  );

  // Implement the sendCallRecordUpdate function
  const sendCallRecordUpdate = useCallback((data: CallRecordUpdateData) => {
    if (!sendRef.current) {
      console.error("WebSocket not connected");
      return;
    }

    const message = {
      event: "callRecordUpdated",
      callId: data.callId,
      userId: data.userId,
      tabId: window.sessionStorage.getItem("tabId") || undefined,
    } as any;

    sendRef.current(message);
    console.log("Sent call record update:", message);
  }, []);

  // New function to send transcription ready message
  const sendTranscriptionReady = useCallback(
    (callId: string, transcriptionId: string) => {
      if (!sendRef.current) {
        console.error("WebSocket not connected");
        return;
      }

      const message: WebSocketMessage = {
        event: "callRecordUpdated",
        callId,
        transcriptionId,
        tabId: window.sessionStorage.getItem("tabId") || undefined,
      };

      sendRef.current(message);
      console.log("Sent transcription ready notification:", message);
    },
    []
  );

  // Create the context value
  const contextValue: WebSocketContextType = useMemo(
    () => ({
      isConnected,
      reconnect,
      sendCallRecordUpdate,
      sendTranscriptionReady,
      waitUntilConnected,
    }),
    [
      isConnected,
      reconnect,
      sendCallRecordUpdate,
      sendTranscriptionReady,
      waitUntilConnected,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </WebSocketContext.Provider>
  );
};

export default WebSocket;